import Image from "next/image";
import React, { useCallback, useEffect, useState } from "react";
import styles from "./home-featured-testimonials.module.scss";

export type HomeFeaturedTestimonial = {
  id: string;
  title: string;
  body: string;
  author: string;
  imageSrc: string;
  imageAlt: string;
  /** If set, image is clickable and opens the parent video dialog with this URL (e.g. mp4). */
  videoSrc?: string | null;
  isNotVideo?: boolean;
};

export const DEFAULT_TESTIMONIALS: HomeFeaturedTestimonial[] = [
  {
    id: "corgi",
    title: "Middleware reduced the time spent debugging and resolving issues by nearly 90%.",
    body:
      "What sets their AI apart is that it does not stop at detecting issues. It actually helps fix problems in production, and for engineering teams, that's been a real game changer. At Corgi, we rely on Middleware to monitor our entire infrastructure and application stack.",
    author: "Nico Laqua, CEO at Corgi",
    imageSrc: "/images/corgi-testimonial.webp",
    imageAlt: "Nico Laqua, CEO at Corgi",
    videoSrc: "/images/corgi-testimonial-demo.mp4",
  },
  {
    id: "eragon",
    title: "We chose Middleware because of their cutting edge AI capabilities.",
    body:
      "As companies continue to scale and deploy agents on eragon, it's critical that we get notified of any bugs or any issues within milliseconds. We use Middleware's AI capabilities and observability platform to do that, so we can continue to focus on our customers.",
    author: "Josh Sirota, CEO at Eragon",
    imageSrc: "/images/eragon-testimonial.webp",
    imageAlt: "Josh Sirota, CEO at Eragon",
    videoSrc: "/images/eragon-testimonial-mw.mp4",
  },
];

type Props = {
  items?: HomeFeaturedTestimonial[];
  /** Called with MP4 (or other) URL when user clicks a slide that has `videoSrc`. */
  onVideoOpen: (src: string, isNotVideo?: boolean) => void;
};

export default function HomeFeaturedTestimonials({
  items = DEFAULT_TESTIMONIALS,
  onVideoOpen,
}: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animNonce, setAnimNonce] = useState(0);

  const len = items.length;
  const active = items[activeIndex] ?? items[0];

  const go = useCallback(
    (delta: number) => {
      if (len <= 1) return;
      setActiveIndex((i) => (i + delta + len) % len);
      setAnimNonce((n) => n + 1);
    },
    [len]
  );

  useEffect(() => {
    if (len <= 1) return;
    const id = window.setInterval(() => {
      setActiveIndex((i) => (i + 1) % len);
      setAnimNonce((n) => n + 1);
    }, 5000);
    return () => window.clearInterval(id);
  }, [len, activeIndex]);

  if (!active) return null;

  return (
    <div className={`mw-review-section ${styles.root}`}>
      <div className="d-flex">
        <div
          key={`${active.id}-${animNonce}`}
          className={`review-content ${styles.contentTextAnimate}`}
        >
          <h3>{active.title}</h3>
          <p>{active.body}</p>
          <div className="review-author">{active.author}</div>
        </div>
        <div
          className={`review-profile ${styles.imageColumn} ${
            active.videoSrc ? "" : styles.staticImage
          }`}
        >
          {active.videoSrc ? (
            <button
              type="button"
              className={styles.imageButton}
              onClick={() => onVideoOpen(active.videoSrc as string, active.isNotVideo)}
              aria-label={`Play testimonial video: ${active.imageAlt}`}
            >
              <Image
                src={active.imageSrc}
                alt={active.imageAlt}
                width={855}
                height={690}
                quality={90}
                sizes="(max-width: 768px) 100vw, 540px"
                loading="lazy"
              />
              
            </button>
          ) : (
            <Image
              src={active.imageSrc}
              alt={active.imageAlt}
              width={855}
              height={690}
              quality={90}
              sizes="(max-width: 768px) 100vw, 540px"
              loading="lazy"
            />
          )}
        </div>
      </div>

      {len > 1 ? (
        <div
          className={styles.carouselNav}
          role="navigation"
          aria-label="Featured testimonials"
        >
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => go(-1)}
            aria-label="Previous testimonial"
          >
            <span className={styles.navIcon} aria-hidden>
              ‹
            </span>
          </button>
          <button
            type="button"
            className={styles.navBtn}
            onClick={() => go(1)}
            aria-label="Next testimonial"
          >
            <span className={styles.navIcon} aria-hidden>
              ›
            </span>
          </button>
        </div>
      ) : null}
    </div>
  );
}
